import React, { useEffect, useState } from "react";
import api from "../lib/api";
import { Send, Check, Brain, Wrench, TrendingUp } from "lucide-react";
import { toast } from "sonner";

const CATEGORIES = [
    { id: "intel", label: "Market Intel", icon: Brain },
    { id: "optimization", label: "Tool Optimization", icon: Wrench },
    { id: "pivot", label: "Creative Pivot", icon: TrendingUp },
];

export default function ScoutReports() {
    const [reports, setReports] = useState([]);
    const [form, setForm] = useState({ title: "", category: "intel", state_code: "", content: "" });
    const [busy, setBusy] = useState(false);

    const load = async () => {
        const { data } = await api.get("/scout-reports");
        setReports(data);
    };

    useEffect(() => { load(); }, []);

    const submit = async (e) => {
        e.preventDefault();
        setBusy(true);
        try {
            await api.post("/scout-reports", {
                ...form,
                state_code: form.state_code.trim() || null,
            });
            setForm({ title: "", category: "intel", state_code: "", content: "" });
            toast.success("Scout report transmitted.");
            load();
        } catch (e) {
            toast.error(e.response?.data?.detail || "Transmission failed");
        } finally {
            setBusy(false);
        }
    };

    return (
        <div className="space-y-8">
            <div className="fade-up">
                <div className="text-[10px] uppercase tracking-[0.35em] text-muted-foreground">Scout Reports</div>
                <h1 className="font-heading text-4xl sm:text-5xl mt-1 tracking-tighter">Transmit Intel</h1>
                <p className="text-muted-foreground mt-2 max-w-2xl">
                    Submit market intelligence, tool optimisations, and creative pivots.
                    The Director reviews and approves for fleet-wide deployment.
                </p>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                {/* Form */}
                <form onSubmit={submit} className="glass rounded-2xl p-6 space-y-4">
                    <div className="font-heading text-xl mb-2">New scout report</div>

                    <div>
                        <label className="text-xs uppercase tracking-wider text-muted-foreground">Title</label>
                        <input
                            required
                            value={form.title}
                            onChange={(e) => setForm({ ...form, title: e.target.value })}
                            data-testid="scout-title"
                            className="mt-1 w-full px-4 py-2.5 rounded-md bg-[hsl(151_55%_9%)] border border-[hsl(40_40%_25%/0.3)] focus:outline-none focus:ring-2 focus:ring-[hsl(40_61%_53%)]"
                            placeholder="e.g. Facebook CPM spiking in TX metros"
                        />
                    </div>

                    <div>
                        <label className="text-xs uppercase tracking-wider text-muted-foreground">Category</label>
                        <div className="grid grid-cols-3 gap-2 mt-1">
                            {CATEGORIES.map((c) => (
                                <button
                                    key={c.id}
                                    type="button"
                                    onClick={() => setForm({ ...form, category: c.id })}
                                    data-testid={`scout-cat-${c.id}`}
                                    className={`p-3 rounded-md text-xs flex flex-col items-center gap-1 transition-colors ${form.category === c.id ? "bg-[hsl(40_61%_25%/0.6)] ring-2 ring-[hsl(40_61%_53%)]" : "bg-[hsl(151_55%_13%)] hover:bg-[hsl(151_55%_16%)]"}`}
                                >
                                    <c.icon size={16} />
                                    {c.label}
                                </button>
                            ))}
                        </div>
                    </div>

                    <div>
                        <label className="text-xs uppercase tracking-wider text-muted-foreground">State (optional)</label>
                        <input
                            value={form.state_code}
                            onChange={(e) => setForm({ ...form, state_code: e.target.value.toUpperCase().slice(0, 2) })}
                            data-testid="scout-state"
                            className="mt-1 w-full px-4 py-2.5 rounded-md bg-[hsl(151_55%_9%)] border border-[hsl(40_40%_25%/0.3)] focus:outline-none focus:ring-2 focus:ring-[hsl(40_61%_53%)] font-mono"
                            placeholder="TX"
                            maxLength={2}
                        />
                    </div>

                    <div>
                        <label className="text-xs uppercase tracking-wider text-muted-foreground">Briefing</label>
                        <textarea
                            required
                            rows={6}
                            value={form.content}
                            onChange={(e) => setForm({ ...form, content: e.target.value })}
                            data-testid="scout-content"
                            className="mt-1 w-full px-4 py-3 rounded-md bg-[hsl(151_55%_9%)] border border-[hsl(40_40%_25%/0.3)] focus:outline-none focus:ring-2 focus:ring-[hsl(40_61%_53%)] resize-none"
                            placeholder="Detail what you've observed, measured, or optimised..."
                        />
                    </div>

                    <button
                        type="submit"
                        disabled={busy}
                        data-testid="scout-submit"
                        className="w-full py-3 rounded-md bg-[hsl(40_61%_53%)] text-[hsl(40_20%_10%)] font-semibold hover:bg-[hsl(45_75%_62%)] transition-colors disabled:opacity-50 inline-flex items-center justify-center gap-2"
                    >
                        <Send size={16} /> {busy ? "Transmitting..." : "Transmit Report"}
                    </button>
                </form>

                {/* List */}
                <div className="glass rounded-2xl p-6">
                    <div className="font-heading text-xl mb-4">Transmission log</div>
                    <div className="space-y-3 max-h-[600px] overflow-y-auto pr-2">
                        {reports.length === 0 && <div className="text-sm text-muted-foreground">No transmissions yet.</div>}
                        {reports.map((r) => {
                            const cat = CATEGORIES.find((c) => c.id === r.category);
                            const CatIcon = cat?.icon || Brain;
                            return (
                                <div key={r.id} className="p-4 rounded-lg bg-[hsl(151_55%_13%)] border border-[hsl(40_40%_25%/0.18)]" data-testid={`scout-item-${r.id}`}>
                                    <div className="flex items-start justify-between gap-3">
                                        <div className="flex items-center gap-2">
                                            <div className="w-7 h-7 rounded-md bg-[hsl(40_61%_22%)] flex items-center justify-center text-[hsl(45_85%_68%)]">
                                                <CatIcon size={14} />
                                            </div>
                                            <div>
                                                <div className="text-sm font-medium">{r.title}</div>
                                                <div className="text-[10px] uppercase tracking-wider text-muted-foreground">
                                                    {cat?.label} · {r.state_code || "fleet-wide"} · by {r.user_name}
                                                </div>
                                            </div>
                                        </div>
                                        {r.status === "approved" ? (
                                            <span className="text-[10px] uppercase tracking-wider text-[hsl(45_85%_68%)] inline-flex items-center gap-1"><Check size={12} /> approved</span>
                                        ) : (
                                            <span className="text-[10px] uppercase tracking-wider text-muted-foreground">{r.status}</span>
                                        )}
                                    </div>
                                    <p className="mt-3 text-sm text-muted-foreground whitespace-pre-wrap line-clamp-4">{r.content}</p>
                                </div>
                            );
                        })}
                    </div>
                </div>
            </div>
        </div>
    );
}
